import { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { translate, DEFAULT_LANGUAGE } from '../i18n';

const LanguageContext = createContext(null);
const API = import.meta.env.VITE_API_URL || '';

function getInitialLanguage() {
  const stored = localStorage.getItem('weship-language');
  if (stored === 'en' || stored === 'de') return stored;
  return DEFAULT_LANGUAGE;
}

/* Map OMS stage names (lowercase) → mapping keys */
const STAGE_NAME_TO_KEY = {
  'response required': 'response_required',
  'waiting for support': 'waiting_for_support',
  'in progress': 'in_progress',
  'clarification with recipient': 'clarification_with_recipient',
  'solution proposed': 'solution_proposed',
  'done': 'done',
  'closed': 'done',
  'fully closed': 'done',
};

/* i18n keys for default stage labels */
const STAGE_I18N = {
  'response required': 'cases.stageResponseRequired',
  'waiting for support': 'cases.stageWaitingSupport',
  'in progress': 'cases.stageInProgress',
  'clarification with recipient': 'cases.stageClarification',
  'solution proposed': 'cases.stageSolutionProposed',
  'done': 'cases.stageDone',
  'closed': 'cases.stageDone',
};

const STAGE_I18N_SHORT = {
  'response required': 'cases.stageShortResponse',
  'waiting for support': 'cases.stageShortWaiting',
  'in progress': 'cases.stageShortInProgress',
  'clarification with recipient': 'cases.stageShortClarification',
  'solution proposed': 'cases.stageShortSolution',
  'done': 'cases.stageShortDone',
  'closed': 'cases.stageShortDone',
};

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(getInitialLanguage);
  // #1809-Fix: Unterscheidet eine eigene Sprachwahl von DEFAULT_LANGUAGE ('en').
  // Die Work-Oberflaeche setzt Deutsch nur als Voreinstellung — ohne dieses Flag
  // koennte sie das nicht tun, ohne eine echte Wahl jedes Mal zu ueberschreiben.
  const [languageIsExplicit, setLanguageIsExplicit] = useState(
    () => localStorage.getItem('weship-language') !== null
  );
  const [stageMapping, setStageMapping] = useState({});

  const setLanguage = useCallback((lang) => {
    setLanguageState(lang);
    setLanguageIsExplicit(true);
    localStorage.setItem('weship-language', lang);
  }, []);

  const toggleLanguage = useCallback(() => {
    setLanguage(language === 'de' ? 'en' : 'de');
  }, [language, setLanguage]);

  // #1127: i18next-style (key, defaultStr, params) ist im Codebase weit verbreitet
  // (z.B. {{count}}/{{total}} Banner auf /mitarbeiter). Wir leiten beide
  // Signaturen an translate() durch — siehe i18n/index.js.
  const t = useCallback((key, defaultOrParams, params) => {
    return translate(language, key, defaultOrParams, params);
  }, [language]);

  const tMonth = useCallback((index) => {
    return translate(language, `common.months.${index}`);
  }, [language]);

  const tMonthShort = useCallback((index) => {
    return translate(language, `common.months_short.${index}`);
  }, [language]);

  /* Load stage mapping once on mount */
  const loadStageMapping = useCallback(() => {
    fetch(`${API}/api/cases/config/oms/stages`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d?.mapping) setStageMapping(d.mapping); })
      .catch(() => {});
  }, []);

  useEffect(() => { loadStageMapping(); }, [loadStageMapping]);

  /* Resolve stage name → custom label or i18n default
     #1220 — Underscore-Form (DB-Enum 'response_required') wird zu Space-Form ('response required')
     normalisiert, damit lokale Cases dieselben Labels erhalten wie OMS-Stages. */
  const getStageLabel = useCallback((stageName) => {
    if (!stageName) return '';
    const lower = stageName.toLowerCase().replace(/_/g, ' ');
    const key = STAGE_NAME_TO_KEY[lower];
    const entry = key ? stageMapping[key] : null;
    if (entry?.custom_label) return entry.custom_label;
    const i18nKey = STAGE_I18N[lower];
    return i18nKey ? translate(language, i18nKey) : stageName;
  }, [stageMapping, language]);

  /* Short variant — custom_label overrides short label too */
  const getStageLabelShort = useCallback((stageName) => {
    if (!stageName) return '';
    const lower = stageName.toLowerCase().replace(/_/g, ' ');
    const key = STAGE_NAME_TO_KEY[lower];
    const entry = key ? stageMapping[key] : null;
    if (entry?.custom_label) return entry.custom_label;
    const i18nKey = STAGE_I18N_SHORT[lower];
    return i18nKey ? translate(language, i18nKey) : stageName;
  }, [stageMapping, language]);

  const locale = language === 'de' ? 'de-DE' : 'en-US';

  const value = useMemo(() => ({
    language,
    languageIsExplicit,
    locale,
    setLanguage,
    toggleLanguage,
    t,
    tMonth,
    tMonthShort,
    stageMapping,
    getStageLabel,
    getStageLabelShort,
    reloadStageMapping: loadStageMapping,
  }), [language, languageIsExplicit, locale, setLanguage, toggleLanguage, t, tMonth, tMonthShort, stageMapping, getStageLabel, getStageLabelShort, loadStageMapping]);

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
}
